import { useRef } from 'react';
import styled from '@emotion/styled';
import {
  motion,
  useMotionTemplate,
  useScroll,
  useTransform,
} from 'framer-motion';

import { SkipToContentButton } from '@/components/common/SkipToContentButton';
import { Footer } from '@/components/layout/Footer';
import { MainContainer } from '@/components/layout/Layout';
import { NavigationBar } from '@/components/layout/NavigationBar';
import { SharedLayout } from '@/components/layout/SharedLayout';

import type { FooterProps } from '@/components/layout/Footer';
import type { SharedLayoutProps } from '@/components/layout/SharedLayout';
import type { SanityNavigationType } from '@/types/sanity';
import type { Locale } from '@/types/shared';

interface BlogLayoutProps extends SharedLayoutProps {
  locale?: Locale;
  preview?: boolean;
  siteSettings: FooterProps;
  doNotIndex?: boolean;
  progressWidth?: number;
  navigation?: SanityNavigationType;
}

export const BlogLayout = ({
  children,
  preview = false,
  siteSettings,
  doNotIndex,
  navigation,
  SEO,
  recommendationContext,
  localeTranslations,
}: BlogLayoutProps) => {
  const mainRef = useRef<HTMLElement | null>(null);
  const { scrollYProgress } = useScroll();
  const scrollTransform = useTransform(scrollYProgress, [0, 1], [0, 100]);

  const isIntercomEnabled = !!siteSettings?.isIntercomEnabled;
  const SEOWithNoIndex = doNotIndex ? { ...SEO, noindex: true } : SEO;

  return (
    <SharedLayout
      localeTranslations={localeTranslations}
      isIntercomEnabled={isIntercomEnabled}
      recommendationContext={recommendationContext}
      SEO={SEOWithNoIndex}
      preview={preview}
    >
      <SkipToContentButton focusElement={mainRef} />

      <ProgressBar
        style={{
          background: useMotionTemplate`linear-gradient( to right, var(--decorative-ultraviolet) ${scrollTransform}%, transparent 0 )`,
        }}
      />
      <NavigationBar navigation={navigation} />

      <MainContainer ref={mainRef} tabIndex={-1}>
        {children}
      </MainContainer>

      <Footer {...siteSettings} />
    </SharedLayout>
  );
};

const ProgressBar = styled(motion.div)`
  top: 0;
  left: 0;
  position: fixed;

  width: 100%;
  height: 6px;
  z-index: 100;
`;
